import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '../ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { InfoDot } from './InfoDot';

export interface FormSelectOption {
  value: string;
  label: string;
}

export interface FormSelectFieldProps<TFieldValues extends FieldValues> {
  /** Field path; the component reads `control` from the surrounding `FormProvider`. */
  name: FieldPath<TFieldValues>;
  label: string;
  options: readonly FormSelectOption[];
  /** Shown when no value is selected. */
  placeholder?: string;
  /** Optional info tooltip beside the label. */
  info?: string;
  disabled?: boolean;
  /** Resolve a validation message (often an i18n key) to display text. Defaults to identity. */
  translateError?: (message: string) => string;
}

/**
 * Generic Controller-wrapped Select following the Field + FieldError pattern — the select
 * counterpart to {@link FormTextField}. App- and i18n-namespace-agnostic; pair with
 * react-hook-form's `FormProvider`.
 */
export function FormSelectField<TFieldValues extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  info,
  disabled,
  translateError,
}: FormSelectFieldProps<TFieldValues>) {
  return (
    <Controller<TFieldValues>
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <div className="flex min-h-6 items-center gap-1">
            <FieldLabel htmlFor={name}>{label}</FieldLabel>
            {info && <InfoDot text={info} />}
          </div>
          <Select
            value={typeof field.value === 'string' ? field.value : ''}
            onValueChange={field.onChange}
            disabled={disabled}
          >
            <SelectTrigger id={name} className="w-full" aria-invalid={fieldState.invalid} onBlur={field.onBlur}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldState.error?.message && (
            <FieldError>
              {translateError ? translateError(fieldState.error.message) : fieldState.error.message}
            </FieldError>
          )}
        </Field>
      )}
    />
  );
}
